import { z } from 'zod';

import {
  LocationListItemSchema,
  RegularLocationListItemSchema,
} from '@/lib/api/schemas/location';
import { e164PhoneSchema, zodDuration, zodTimeStamp } from '@/utils/zodUtils';

export enum PaymentMethodType {
  CARD = 'card',
  US_BANK_ACCOUNT = 'us_bank_account',
}

const basePaymentMethodSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(PaymentMethodType),
  customer: z.string(),
});

export const PaymentMethodSchema = z.discriminatedUnion('type', [
  basePaymentMethodSchema.merge(
    z.object({
      type: z.literal(PaymentMethodType.CARD),
      details: z.object({
        last4: z.string(),
        brand: z.string(),
      }),
    })
  ),
  basePaymentMethodSchema.merge(
    z.object({
      type: z.literal(PaymentMethodType.US_BANK_ACCOUNT),
      details: z.object({
        account_type: z.string(),
        bank_name: z.string(),
        last4: z.string(),
      }),
    })
  ),
]);

export const BillingTypeSchema = z.enum(['count', 'usage']);
export type BillingType = z.infer<typeof BillingTypeSchema>;

export const BillingSchema = z.object({
  enabled: z.boolean(),
  is_billing_required: z.boolean(),
  monthly_quantity: z.number(),
  monthly_saas_fee: z.number(),
  payment_method: PaymentMethodSchema.nullable(),
  quantity_tracker: z.record(z.string(), z.number()).nullable(),
  is_waiving_parking_fee: z.boolean(),
  // Empty string / null from legacy API rows fall back to "count" per spec
  // ("Defaults to 'count' for backward compatibility").
  billing_type: z.preprocess(
    (val) => (val === '' || val == null ? undefined : val),
    BillingTypeSchema.default('count')
  ),
  monthly_parking_charge_day: z.preprocess(
    (val) => (val === '' || val == null ? undefined : val),
    z.coerce.number().int().min(1).max(31).default(7)
  ),
  setup: z
    .object({
      setup_intent_id: z.string(),
      client_secret: z.string(),
    })
    .optional(),
});
export const SubLocationSchema = z.object({
  subLocationId: z.string(),
  blockFaceId: z.string().uuid().nullish(),
  duration: z.preprocess(
    (val) => (val === '' ? null : val),
    zodDuration.nullable()
  ),
  approvalCode: z.string(),
  displayName: z.string(),
  validationAccessCode: z.string(),
  approvalCodeLastUpdated: zodTimeStamp.nullable(),
  validationAccessCodeDistributionList: z.array(z.string()).nullable(),
  validationAccessCodeSMSDistributionList: z.array(e164PhoneSchema).nullable(),
  validationAccessCodeLastUpdated: zodTimeStamp.nullable(),
  billing: BillingSchema,
  validationTimeGateMinutes: z.preprocess(
    (val) => (val == null ? null : val),
    z.coerce.number().int().min(0).nullable()
  ),
  // Per-sublocation ceiling (minutes) used by usage-based validations to size
  // the preauth at creation and to cap the final stay at termination. We
  // intentionally keep this separate from Location.max_time so toggling the
  // validation ceiling cannot perturb driver-paid Stripe preauth, the pricing
  // matrix, or camera event processing -- which still consult max_time. Only
  // meaningful when `duration === 'usage'`; the API ignores it otherwise.
  maxValidationMins: z
    .preprocess(
      (val) => (val === '' || val == null ? null : val),
      z.coerce.number().int().min(1).nullable()
    )
    .optional(),
  maxValidationsPerEightHours: z
    .preprocess(
      (val) => (val === '' || val == null ? null : val),
      z.coerce
        .number()
        .int()
        .min(1, { message: 'Must be at least 1' })
        .nullable()
    )
    .optional(),
});

export const UpdateSubLocationSchema = SubLocationSchema.merge(
  z.object({
    billing: BillingSchema.merge(
      z.object({
        payment_method: z
          .object({
            setup_intent_id: z.string(),
            client_secret: z.string(),
          })
          .optional(),
      })
    ),
  })
);
/** Portal-only: when the user picks a location group as one unit (permits UI). */
export const LocationGroupMetadataSchema = z.object({
  key: z.string(),
  displayLabel: z.string(),
  memberLocationIds: z.array(z.string()),
});

export type LocationGroupMetadata = z.infer<typeof LocationGroupMetadataSchema>;

export const SublocationsOfLocationSchema = z.object({
  locationId: z.string(),
  // "Location Only" locations can come back without a `sublocations` array
  // (null/absent). Normalize to [] at the boundary so downstream consumers
  // always receive an array and can't crash on it (ENG-1714).
  sublocations: z.preprocess((val) => val ?? [], z.array(SubLocationSchema)),
  validationRotateAccessCode: z.boolean(),
  locationNickname: z.string().optional(), // this is not API field
  locationGroup: LocationGroupMetadataSchema.optional(),
  // Portal-only: the main lot's own root scope (`has_access_to_root_location`),
  // tracked apart from the sub-lot picks so "root only" and "root + some sub-lots"
  // survive a round trip. Absent for pickers that do not expose the root axis.
  hasAccessToRootLocation: z.boolean().optional(),
});
export const SubLocationsOfLocationListSchema = z.array(
  SublocationsOfLocationSchema
);
export type SubLocationsOfLocationListType = z.infer<
  typeof SubLocationsOfLocationListSchema
>;
export type SublocationsOfLocationType = z.infer<
  typeof SublocationsOfLocationSchema
>;
export type SublocationsOfRegularLocationTypeExtended =
  SublocationsOfLocationType & {
    location: z.infer<typeof RegularLocationListItemSchema>;
  };

export type SublocationsOfLocationTypeExtended = SublocationsOfLocationType & {
  location: z.infer<typeof LocationListItemSchema>;
};
export type SubLocationType = z.infer<typeof SubLocationSchema>;

export const RotationFrequencySchema = z.enum([
  'daily',
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
]);
export type RotationFrequency = z.infer<typeof RotationFrequencySchema>;
const rotationTimeSchema = z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/);

// Rotation Schedule Schema
// Uses union to ensure proper validation based on enabled/frequency state
export const RotationScheduleSchema = z.union([
  // When disabled, all fields are optional
  z.object({
    enabled: z.literal(false),
    frequency: RotationFrequencySchema.or(z.literal('')),
    day_of_week: z.number().int().min(1).max(7).optional(),
    time: rotationTimeSchema.optional(),
    last_scheduled_rotation_at: z.string().nullable().optional(),
  }),
  // Daily/monthly/quarterly/yearly when enabled: time required, day_of_week not allowed
  z.object({
    enabled: z.literal(true),
    frequency: RotationFrequencySchema.extract([
      'daily',
      'monthly',
      'quarterly',
      'yearly',
    ]),
    time: rotationTimeSchema,
    last_scheduled_rotation_at: z.string().nullable().optional(),
  }),
  // Weekly frequency when enabled: both time and day_of_week required
  z.object({
    enabled: z.literal(true),
    frequency: RotationFrequencySchema.extract(['weekly']),
    day_of_week: z.number().int().min(1).max(7),
    time: rotationTimeSchema,
    last_scheduled_rotation_at: z.string().nullable().optional(),
  }),
]);

export type RotationScheduleType = z.infer<typeof RotationScheduleSchema>;
